import { Order } from "../../order.model";
import {
  IOrderData,
  OrderFactoryDayOfWeekData,
  OrderFactoryProductData,
  OrderFactoryEventData,
  OrderFactoryVenueData,
  OrderFactoryCityData
} from "../data/data.abstract";
import { ProductType } from "src/app/core/product/enums";
import { VenueSummary } from "../../models/venue-summary.model";
import * as moment from "moment";

const eventDayOfWeekForOrder = (order: Order): number =>
  moment.tz(order.event.date, order.event.timezone as string).day();

export abstract class ITableSummary {
  protected _data: Map<number | string, IOrderData>;
  constructor() {
    this._data = new Map();
  }

  public key = (order: Order): number | string => null; //OVERRIDE
  public entry = (order: Order) => null; //OVERRIDE
  // public next = (order: Order): void => this.add(order);
  // protected filter = (order: Order): boolean => order.reference.id != null; //OVERRIDE

  public add(order: Order) {
    if (!this.exists(order)) {
      this._data.set(this.key(order), this.entry(order));
    }
    this._data.get(this.key(order)).add(this.key(order), order);
  }
  protected exists = (order: Order) => this._data.has(this.key(order));

  public get data(): IOrderData[] {
    return Array.from(this._data.values());
  }
}

export class OrderFactoryDayOfWeekSummary extends ITableSummary {
  protected _data: Map<number, OrderFactoryDayOfWeekData>;
  constructor() {
    super();
  }
  public key = (order: Order) => eventDayOfWeekForOrder(order);
  public entry = (order: Order) =>
    new OrderFactoryDayOfWeekData(
      eventDayOfWeekForOrder(order),
      eventDayOfWeekForOrder(order)
    );

  public get data(): OrderFactoryDayOfWeekData[] {
    return Array.from(this._data.values());
  }
}

export class OrderFactoryProductSummary extends ITableSummary {
  protected _data: Map<ProductType, OrderFactoryProductData>;

  constructor() {
    super();
  }
  public key = (order: Order) => order.product.type;
  public entry = (order: Order) =>
    new OrderFactoryProductData(order.product.type);

  public get data(): OrderFactoryProductData[] {
    return Array.from(this._data.values()).sort(
      (a, b) => b.revenue - a.revenue
    );
  }
}

export class OrderFactoryEventSummary extends ITableSummary {
  protected _data: Map<string, OrderFactoryEventData>;
  constructor() {
    super();
  }
  public key = (order: Order) =>
    order.event.reference
      ? order.event.reference.id
      : moment(order.summary.purchasedAt).format("MM-DD-YY");
  public entry = (order: Order) =>
    new OrderFactoryEventData(this.key(order), eventDayOfWeekForOrder(order));

  public get data(): OrderFactoryEventData[] {
    return Array.from(this._data.values());
  }
}

export class OrderFactoryVenueSummary extends ITableSummary {
  protected _data: Map<string, OrderFactoryVenueData>;
  constructor() {
    super();
  }
  public key = (order: Order) => order.venue.reference.id;
  public entry = (order: Order) =>
    new OrderFactoryVenueData(
      order.venue.reference.id,
      order.venue,
      order.city
    );

  public get data(): OrderFactoryVenueData[] {
    return Array.from(this._data.values()).sort(
      (a, b) => b.revenue - a.revenue
    );
  }
}

export class OrderFactoryCitySummary extends ITableSummary {
  protected _data: Map<string, OrderFactoryCityData>;

  constructor() {
    super();
  }
  public key = (order: Order) => order.city.reference.id;
  public entry = (order: Order) =>
    new OrderFactoryCityData(order.city.reference.id, order.city);

  public get data(): OrderFactoryCityData[] {
    return Array.from(this._data.values()).sort(
      (a, b) => b.revenue - a.revenue
    );
  }
}

export class OrderFactoryPeriodSummary {
  public _venues: Map<string, VenueSummary>;
  private _nUsers: Map<string, number>; //New Users
  private _uUsers: Map<string, number>; //Unique Users
  public units: number;
  public revenue: number;
  public fees: number;
  public tips: number;
  public redeemedUnits: number;
  public subtotal: number;
  public orders: number;
  constructor() {
    this._venues = new Map();
    this._nUsers = new Map();
    this._uUsers = new Map();
    this.units = 0;
    this.revenue = 0;
    this.fees = 0;
    this.tips = 0;
    this.redeemedUnits = 0;
    this.orders = 0;
    this.subtotal = 0;
  }

  async add(order: Order) {
    this.units += order.summary.units;
    this._uUsers.set(order.user.reference.id, 0);
    this._venues.set(order.venue.reference.id, order.venue);
    this.orders += 1;
    this.revenue += order.summary.total;
    if (order.summary.redeemedAt != null)
      this.redeemedUnits += order.summary.units;
    if (order.summary.tip != null) this.tips += order.summary.tip;

    // INCREMENT SUBTOTAL & FEE DEPENDING ON SOME SHIT

    if (order.menu != null) {
      this.subtotal += order.summary.subtotal ?? 0;
      this.fees += order.summary.serviceFee ?? 0;
    } else {
      //CHECK IF PERCENT FEE OR FLAT FEE (if SUBTOTAL)
      var subtotal = order.summary.price * order.summary.units;
      var tip = order.summary.tip ?? 0;
      this.subtotal += subtotal;
      this.fees += order.summary.total - tip - subtotal;
    }
  }

  // public getFee(order: Order) {
  //   if (order.menu != null) {
  //     return order.
  //   }
  // }

  public get nUsers(): number {
    return this._nUsers.size;
  }
  public get uUsers(): number {
    return this._uUsers.size;
  }
  public get venues(): number {
    return this._venues.size;
  }
  public get avgPrice(): number {
    return this.units == 0 ? 0 : this.subtotal / this.units;
  }
  public get tipPercent(): number {
    return this.subtotal == 0 ? 0 : this.tips / this.subtotal;
  }
  public get redeemPercent(): number {
    return this.units == 0 ? 0 : this.redeemedUnits / this.units;
  }
}
